/**
 * SystemCommonCodeManagementPage Component
 * 공통코드 관리 페이지 - 그룹별 공통코드 조회
 */
import React, { useEffect, useCallback } from 'react';
import PrimaryButton from '@/shared/components/buttons/PrimaryButton';
import Loading from '@/shared/components/feedback/Loading';
import { useSystemCommonCode } from '../hooks/useSystemCommonCode';
import { DASHBOARD_EVENTS } from '../../shared/config/system.config';
import GroupTable from './GroupTable';
import {
    navigateToAddPage,
    LOADING_MESSAGES,
} from './commonCodeManagement.helpers';
import '../styles/SystemCommonCodeManagementPage.css';

// ======== 컴포넌트 ========

const SystemCommonCodeManagementPage: React.FC = () => {
    // ======== 훅 ========

    const {
        groups,
        groupsLoading,
        groupsExpanded,
        fetchGroups,
        expandedGroupCodes,
        groupCodesCache,
        groupCodesLoading,
        toggleGroupCodes,
    } = useSystemCommonCode();

    // ======== 이벤트 리스너 ========

    /**
     * 공통코드 저장 완료 후 데이터 재조회 이벤트 리스너
     */
    useEffect(() => {
        const handleRefresh = () => fetchGroups();

        window.addEventListener(DASHBOARD_EVENTS.COMMON_CODE_REFRESH, handleRefresh);
        return () => {
            window.removeEventListener(DASHBOARD_EVENTS.COMMON_CODE_REFRESH, handleRefresh);
        };
    }, [fetchGroups]);

    // ======== 핸들러 ========

    /**
     * 공통코드 추가 버튼 클릭
     */
    const handleAddClick = useCallback(() => {
        navigateToAddPage();
    }, []);

    // ======== 렌더링 ========

    return (
        <div className="common-code-management-page">
            {/* 페이지 헤더 */}
            <div className="page-header">
                <h1 className="page-title">공통코드 관리</h1>
            </div>

            {/* 조회 버튼 섹션 */}
            <div className="query-section">
                <div className="query-button-wrapper">
                    <PrimaryButton
                        onClick={handleAddClick}
                        showArrow={false}
                        className="add-button"
                    >
                        <i className="fas fa-plus add-button-icon"></i>
                        공통코드 추가
                    </PrimaryButton>
                </div>

                {/* 그룹 조회 버튼 */}
                <button
                    className={`query-button ${groupsExpanded ? 'expanded' : ''}`}
                    onClick={fetchGroups}
                    disabled={groupsLoading}
                >
                    <div className="query-button-content">
                        <i className="fas fa-layer-group"></i>
                        <span>공통코드 그룹 조회</span>
                    </div>
                    <i className={`fas fa-chevron-down query-arrow ${groupsExpanded ? 'expanded' : ''}`}></i>
                </button>

                {/* 그룹 테이블 (펼침 시) */}
                {groupsExpanded && (
                    <div className="query-result">
                        {groupsLoading ? (
                            <div className="query-loading">
                                <Loading message={LOADING_MESSAGES.GROUPS} />
                            </div>
                        ) : (
                            <GroupTable
                                groups={groups}
                                expandedGroupCodes={expandedGroupCodes}
                                groupCodesCache={groupCodesCache}
                                groupCodesLoading={groupCodesLoading}
                                onToggleGroup={toggleGroupCodes}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SystemCommonCodeManagementPage;
